// src/pages/enquiries/EnquiryList.js
// ZENO ERP — Enquiry / Lead List
// Roles: Owner + SM + Manager

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs, addDoc, query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  notifyEnquiryFollowUp,
  markNotificationReadByReference,
} from "../../services/notificationService";

const ENQUIRY_TYPES   = ["Installation", "AMC", "Service", "Repair", "Other"];
const ENQUIRY_SOURCES = ["Phone", "Reference", "Walk-in", "Website", "WhatsApp"];
const ENQUIRY_STATUS  = ["New", "In Discussion", "Quoted", "Won", "Lost"];

const STATUS_UI = {
  "New":            { bg: "#dbeafe", color: "#1e40af", dot: "#3b82f6" },
  "In Discussion":  { bg: "#fef9c3", color: "#854d0e", dot: "#eab308" },
  "Quoted":         { bg: "#ede9fe", color: "#5b21b6", dot: "#8b5cf6" },
  "Won":            { bg: "#dcfce7", color: "#166534", dot: "#22c55e" },
  "Lost":           { bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
};

const emptyForm = {
  client_name: "", phone: "", email: "",
  location: "", enquiry_type: "Installation",
  source: "Phone", description: "",
  assigned_to_name: "", follow_up_date: "",
  estimated_value: "", status: "New",
};

export default function EnquiryList() {
  const navigate  = useNavigate();
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [enquiries,      setEnquiries]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [showCreate,     setShowCreate]     = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [filter,         setFilter]         = useState("All");
  const [search,         setSearch]         = useState("");
  const [users,          setUsers]          = useState([]);
  const [form,           setForm]           = useState(emptyForm);
  const [dupWarning,     setDupWarning]     = useState(""); // duplicate phone warning

  useEffect(() => { fetchEnquiries(); fetchUsers(); }, []); // eslint-disable-line

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(query(
        collection(db, "enquiries"),
        where("company_id", "==", companyId)
      ));
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.created_at?.seconds ?? 0) - (a.created_at?.seconds ?? 0));
      setEnquiries(list);

      // ── Follow-up due today → bell notification (duplicate safe) ──
      const today = new Date().toISOString().slice(0, 10);
      const dueToday = list.filter(
        e => e.follow_up_date === today && !["Won", "Lost"].includes(e.status) && !e.converted
      );
      if (dueToday.length > 0) {
        // Check existing notifications for today to avoid duplicates
        const existingSnap = await getDocs(query(
          collection(db, "notifications"),
          where("company_id",  "==", companyId),
          where("type",        "==", "enquiry_followup"),
          where("is_read",     "==", false),
          where("resolved",    "==", false)
        ));
        const alreadyNotified = new Set(
          existingSnap.docs.map(d => d.data().reference_id)
        );
        for (const enq of dueToday) {
          if (!alreadyNotified.has(enq.id)) {
            await notifyEnquiryFollowUp({
              enquiry_id:     enq.id,
              enquiry_no:     enq.enquiry_no,
              client_name:    enq.client_name,
              follow_up_date: enq.follow_up_date,
              company_id:     companyId,
            });
          }
        }
      }
      // ── End follow-up trigger ──

    } catch (err) {
      console.error("fetchEnquiries error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, "users"),
        where("company_id", "==", companyId)
      ));
      const EXCLUDED_ROLES = ["technician", "tech", "store", "driver"];
      setUsers(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(u => !EXCLUDED_ROLES.includes((u.role || "").toLowerCase()))
      );
    } catch (err) {
      console.error("fetchUsers error:", err);
    }
  };

  // ── Phone duplicate check ──────────────────────────────
  const checkPhoneDuplicate = (phone) => {
    if (!phone || phone.length < 6) { setDupWarning(""); return; }
    const existing = enquiries.find(e =>
      e.phone?.replace(/\s/g, "") === phone.replace(/\s/g, "") &&
      !["Won", "Lost"].includes(e.status)
    );
    if (existing) {
      setDupWarning(`⚠️ Existing lead: ${existing.client_name} — ${existing.status} (${existing.enquiry_no})`);
    } else {
      setDupWarning("");
    }
  };

  // ── Generate ENQ number ────────────────────────────────
  const generateENQNo = async () => {
    const snap = await getDocs(query(
      collection(db, "enquiries"),
      where("company_id", "==", companyId)
    ));
    return `ENQ-${String(snap.size + 1).padStart(4, "0")}`;
  };

  // ── Create enquiry ─────────────────────────────────────
  const handleCreate = async () => {
    if (!form.client_name.trim()) { alert("Client name required"); return; }
    if (!form.phone.trim())       { alert("Phone required"); return; }
    if (!form.description.trim()) { alert("Requirement description required"); return; }

    setSaving(true);
    try {
      const enqNo = await generateENQNo();
      await addDoc(collection(db, "enquiries"), {
        company_id:       companyId,
        enquiry_no:       enqNo,
        client_name:      form.client_name.trim(),
        phone:            form.phone.trim(),
        email:            form.email.trim(),
        location:         form.location.trim(),
        enquiry_type:     form.enquiry_type,
        source:           form.source,
        description:      form.description.trim(),
        assigned_to_name: form.assigned_to_name,
        follow_up_date:   form.follow_up_date,
        estimated_value:  form.estimated_value ? Number(form.estimated_value) : 0,
        status:           "New",
        converted:        false,
        remarks_log:      [],
        created_by:       user.uid || user.id || "",
        created_by_name:  user.name || "",
        created_at:       serverTimestamp(),
        updated_at:       serverTimestamp(),
      });
      alert(`✅ Enquiry ${enqNo} created`);
      setShowCreate(false);
      setForm(emptyForm);
      setDupWarning("");
      fetchEnquiries();
    } catch (err) {
      console.error("Create enquiry error:", err);
      alert(err.message || "Failed to create enquiry");
    } finally {
      setSaving(false);
    }
  };

  // ── Filter + Search ────────────────────────────────────
  const filtered = enquiries.filter(e => {
    const matchFilter = filter === "All" || e.status === filter;
    const matchSearch = !search ||
      e.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      e.phone?.includes(search) ||
      e.enquiry_no?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  // ── Counts ─────────────────────────────────────────────
  const counts = {};
  ENQUIRY_STATUS.forEach(s => {
    counts[s] = enquiries.filter(e => e.status === s).length;
  });

  // ── Follow-up due today ────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  const dueTodayCount = enquiries.filter(
    e => e.follow_up_date === today && !["Won", "Lost"].includes(e.status)
  ).length;

  return (
    <div className="p-4 pb-20 bg-gray-50 min-h-screen">

      {/* HEADER */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Enquiries</h1>
          {dueTodayCount > 0 && (
            <p className="text-xs text-amber-600 font-semibold mt-0.5">
              ⏰ {dueTodayCount} follow-up{dueTodayCount > 1 ? "s" : ""} due today
            </p>
          )}
        </div>
        <button onClick={() => setShowCreate(true)}
          className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-md">
          + New Enquiry
        </button>
      </div>

      {/* STATS ROW */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        {ENQUIRY_STATUS.map(s => {
          const ui = STATUS_UI[s];
          return (
            <div key={s} onClick={() => setFilter(filter === s ? "All" : s)}
              className="bg-white rounded-xl border border-slate-100 shadow-sm p-3 cursor-pointer hover:shadow-md transition"
              style={{ borderTop: `3px solid ${ui.dot}` }}>
              <p className="text-lg font-bold text-slate-800">{counts[s] || 0}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s}</p>
            </div>
          );
        })}
      </div>

      {/* SEARCH + FILTER TABS */}
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          type="text" placeholder="Search name, phone, ENQ no..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-300 w-full md:w-64"
        />
        <div className="flex gap-2 flex-wrap">
          {["All", ...ENQUIRY_STATUS].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                filter === s
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-600 border-slate-200"
              }`}>
              {s} {s !== "All" && counts[s] ? `(${counts[s]})` : ""}
            </button>
          ))}
        </div>
      </div>

      {/* LIST */}
      {loading ? (
        <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center mt-16">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-slate-500">No enquiries found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(enq => {
            const ui = STATUS_UI[enq.status] || STATUS_UI["New"];
            const isFollowUpDue = enq.follow_up_date === today && !["Won", "Lost"].includes(enq.status);
            return (
              <div key={enq.id}
                onClick={() => navigate(`/enquiries/${enq.id}`)}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 cursor-pointer hover:shadow-md transition"
                style={{ borderLeft: `4px solid ${ui.dot}` }}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0 mr-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-slate-800">{enq.client_name}</p>
                      <span className="text-xs text-slate-400">{enq.enquiry_no}</span>
                      {isFollowUpDue && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">
                          ⏰ Follow-up Today
                        </span>
                      )}
                      {enq.converted && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                          ✅ Converted
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">{enq.phone} {enq.location && `• ${enq.location}`}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {enq.enquiry_type} • {enq.source}
                      {enq.assigned_to_name && ` • 👤 ${enq.assigned_to_name}`}
                      {enq.estimated_value > 0 && ` • ₹${Number(enq.estimated_value).toLocaleString("en-IN")}`}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5 truncate">{enq.description}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-xs font-bold px-3 py-1 rounded-full"
                      style={{ background: ui.bg, color: ui.color }}>
                      {enq.status}
                    </span>
                    {enq.follow_up_date && (
                      <p className="text-xs text-slate-400 mt-1">📅 {enq.follow_up_date}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE MODAL */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="font-bold text-slate-800">New Enquiry</h2>
              <button onClick={() => { setShowCreate(false); setForm(emptyForm); setDupWarning(""); }}
                className="text-slate-400 hover:text-slate-700 text-xl">✕</button>
            </div>
            <div className="p-5 space-y-3">

              {/* Client + Phone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Client Name *</label>
                  <input placeholder="Client name"
                    value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Phone *</label>
                  <input placeholder="Phone number"
                    value={form.phone}
                    onChange={e => {
                      setForm(f => ({ ...f, phone: e.target.value }));
                      checkPhoneDuplicate(e.target.value);
                    }}
                    className={`w-full border rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400 ${
                      dupWarning ? "border-amber-400" : "border-slate-200"
                    }`} />
                  {/* Duplicate warning */}
                  {dupWarning && (
                    <p className="text-xs text-amber-600 mt-1 font-medium">{dupWarning}</p>
                  )}
                </div>
              </div>

              {/* Email + Location */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Email</label>
                  <input placeholder="Email (optional)"
                    value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Location</label>
                  <input placeholder="Area / site location"
                    value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400" />
                </div>
              </div>

              {/* Type + Source */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Enquiry Type</label>
                  <select value={form.enquiry_type} onChange={e => setForm(f => ({ ...f, enquiry_type: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400 bg-white">
                    {ENQUIRY_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Source</label>
                  <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400 bg-white">
                    {ENQUIRY_SOURCES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Requirement *</label>
                <textarea rows={3} placeholder="Describe the requirement..."
                  value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm resize-none outline-none focus:ring-2 focus:ring-cyan-400" />
              </div>

              {/* Assigned + Follow-up */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Assigned To</label>
                  <select value={form.assigned_to_name} onChange={e => setForm(f => ({ ...f, assigned_to_name: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400 bg-white">
                    <option value="">— Select —</option>
                    {users.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Follow-up Date</label>
                  <input type="date" value={form.follow_up_date}
                    onChange={e => setForm(f => ({ ...f, follow_up_date: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400" />
                </div>
              </div>

              {/* Estimated Value */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Estimated Value ₹</label>
                <input type="number" min="0" placeholder="Optional"
                  value={form.estimated_value} onChange={e => setForm(f => ({ ...f, estimated_value: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400" />
              </div>

              <button onClick={handleCreate} disabled={saving}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold text-sm transition disabled:opacity-50">
                {saving ? "Creating..." : "Create Enquiry"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
